import React, { useState } from 'react';
import { motion, useMotionValue, useSpring, AnimatePresence } from 'framer-motion';

const API_URL = 'https://waitlistapi-me2fgadysa-em.a.run.app/api/wailist/creators';

/* Animated checkmark path */
function Checkmark() {
  return (
    <motion.svg
      width="28" height="28" viewBox="0 0 28 28"
      fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ marginBottom: '10px' }}
    >
      <motion.circle
        cx="14" cy="14" r="12"
        stroke="var(--text-dim)" strokeWidth="1"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      />
      <motion.path
        d="M9 14.5l3.5 3.5 6.5-7"
        stroke="var(--text)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.45, ease: 'easeOut', delay: 0.35 }}
      />
    </motion.svg>
  );
}

export default function WaitlistForm() {
  const [email, setEmail]   = useState('');
  const [status, setStatus] = useState('idle');

  /* Magnetic spring */
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 180, damping: 14 });
  const sy = useSpring(my, { stiffness: 180, damping: 14 });

  function onMouseMove(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - (rect.left + rect.width  / 2)) * 0.22);
    my.set((e.clientY - (rect.top  + rect.height / 2)) * 0.22);
  }

  function onMouseLeave() {
    mx.set(0);
    my.set(0);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (!emailRegex.test(email)) return;
    setStatus('loading');
    try {
      const res = await fetch(API_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ EmailId: email, PhoneNumber: '', Message: '' }),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  }

  return (
    <AnimatePresence mode="wait">
      {status === 'success' ? (
        /* ── Success state ── */
        <motion.div
          key="success"
          initial={{ opacity: 0, scale: 0.96, y: 6 }}
          animate={{ opacity: 1, scale: 1,    y: 0 }}
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          style={{
            display:       'flex',
            flexDirection: 'column',
            alignItems:    'center',
            fontFamily:    'var(--font-body)',
            fontSize:      '0.85rem',
            fontWeight:    300,
            color:         'var(--text-dim)',
            whiteSpace:    'nowrap',
          }}
        >
          <Checkmark />
          We'll find you when they're ready.
        </motion.div>
      ) : (
        /* ── Form state ── */
        <motion.div key="form" style={{ x: sx, y: sy }} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
          <form
            onSubmit={handleSubmit}
            noValidate
            aria-label="Join waitlist"
            style={{
              display:      'flex',
              alignItems:   'center',
              overflow:     'hidden',
              borderRadius: '6px',
              border:       '1px solid var(--color-input-border)',
            }}
          >
            <input
              type="email"
              placeholder="your email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              aria-label="Email address"
              aria-invalid={status === 'error'}
              style={{
                background:           'var(--color-input-bg)',
                backdropFilter:       'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border:               'none',
                padding:              '11px 16px',
                fontFamily:           'var(--font-body)',
                fontSize:             '0.85rem',
                fontWeight:           300,
                color:                'var(--text)',
                width:                'clamp(130px, 18vw, 200px)',
                outline:              'none',
                transition:           'background 0.2s, box-shadow 0.2s',
              }}
              onFocus={e  => { e.target.style.background = 'var(--color-input-focus-bg)'; e.target.style.boxShadow = '0 0 0 1px var(--color-input-focus-ring)'; }}
              onBlur={e   => { e.target.style.background = 'var(--color-input-bg)';       e.target.style.boxShadow = 'none'; }}
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              style={{
                background:  'var(--text)',
                border:      'none',
                padding:     '11px 20px',
                fontFamily:  'var(--font-body)',
                fontSize:    '0.85rem',
                fontWeight:  500,
                color:       'var(--bg)',
                cursor:      status === 'loading' ? 'not-allowed' : 'pointer',
                whiteSpace:  'nowrap',
                opacity:     status === 'loading' ? 0.6 : 1,
                transition:  'background 0.25s, transform 0.15s',
              }}
              onMouseEnter={e => { if (status !== 'loading') e.currentTarget.style.background = '#3a3a3e'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--text)'; }}
            >
              {status === 'loading' ? '…' : 'Join the waitlist.'}
            </button>
          </form>

          {/* Error feedback */}
          <AnimatePresence>
            {status === 'error' && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                aria-live="polite"
                style={{
                  textAlign:  'center',
                  color:      '#a85555',
                  fontSize:   '0.78rem',
                  marginTop:  '8px',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 300,
                }}
              >
                Something went wrong — try again.
              </motion.p>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
